import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, isRef as _isRef } from "vue"

const _hoisted_1 = { class: "flex flex-col justify-between w-full py-3 pl-10 pr-7" }
const _hoisted_2 = /*#__PURE__*/_createTextVNode("-")
const _hoisted_3 = /*#__PURE__*/_createTextVNode("+")

import {
  Extra,
  ExtraResponse,
  ScorecardDisplayState,
  ScorecardViewMode,
} from '@/typings/scorecard';
import { computed, toRefs, onErrorCaptured, inject } from 'vue';
import TextField from '@/components/Inputs/TextField.vue';
import ToggleButtons from '@/components/Inputs/ToggleButtons.vue';
import Dropdown from '@/components/Inputs/Dropdown.vue';
import { Participant } from '@/typings/wrestler';
import { useWrestlerStore } from '@/store/wrestlers';
import { useValidation } from '@/composable/useValidation';
import { useScorecardStore } from '@/store/scorecards';

interface Props {
  extra: Extra;
  matchId: number;
  participants: Array<Participant>;
  response: ExtraResponse;
  correctResponse: ExtraResponse;
  disabled?: boolean;
  points: number;
}


export default /*#__PURE__*/_defineComponent({
  props: {
    extra: null,
    matchId: null,
    participants: null,
    response: null,
    correctResponse: null,
    disabled: { type: Boolean },
    points: null
  },
  setup(__props: any) {

const props = __props as {
  extra: Extra;
  matchId: number;
  participants: Array<Participant>;
  response: ExtraResponse;
  correctResponse: ExtraResponse;
  disabled?: boolean;
  points: number;
}

const resultsMode = inject('resultsMode');
const viewMode = inject('viewMode');



const { extra, response, correctResponse, points, matchId } = toRefs(props);

const answer = computed({
  get() {
    if (viewMode === ScorecardViewMode.Public) {
      if (Array.isArray(correctResponse.value.Answer)) {
        // Do a thing
        return correctResponse.value.Answer;
      } else if (isPositiveNumber(correctResponse.value.Answer)) {
        return parseInt(correctResponse.value.Answer.toString(), 10);
      } else {
        switch (extra.value.Type) {
          case 'Wrestler':
          case 'AllWrestlers': {
            const wrestler = wrestlerStore.wrestlers.find(
              (w) => w.Name === correctResponse.value.Answer
            );
            if (wrestler) {
              return wrestler.SuperstarId;
            } else {
              return correctResponse.value.Answer;
            }
          }
          case 'Number':
          case 'Yes/No':
          case 'Multi':
          case 'Fall':
          case 'OverUnder':
          default:
            return correctResponse.value.Answer;
        }
      }
    }
    if (Array.isArray(response.value.Answer)) {
      // Do a thing
      return response.value.Answer;
    } else if (isPositiveNumber(response.value.Answer)) {
      return parseInt(response.value.Answer.toString(), 10);
    } else {
      switch (extra.value.Type) {
        case 'Wrestler':
        case 'AllWrestlers': {
          const wrestler = wrestlerStore.wrestlers.find(
            (w) => w.Name === response.value.Answer
          );
          if (wrestler) {
            return wrestler.SuperstarId;
          } else {
            return response.value.Answer;
          }
        }
        case 'Number':
        case 'Yes/No':
        case 'Multi':
        case 'Fall':
        case 'OverUnder':
        default:
          return response.value.Answer;
      }
    }
  },
  set(value: ExtraResponse['Answer']) {
    response.value.Answer = value;
  },
});

const allowMulti = computed(
  () =>
    resultsMode &&
    ['AllWrestlers', 'Wrestler', 'Multi'].includes(extra.value.Type)
);

const extraComponent = computed(() => {
  switch (extra.value.Type) {
    case 'Number':
      return { component: TextField, type: 'number' };
    case 'Yes/No':
      return {
        component: ToggleButtons,
        type: 'yes/no',
        options: [
          { text: 'Yes', value: 'Yes', clickable: true },
          { text: 'No', value: 'No', clickable: true },
        ],
      };
    case 'Multi':
      return { component: Dropdown, type: 'multi' };
    case 'Fall':
      return { component: Dropdown, type: 'fall' };
    case 'FallNDQ':
      return { component: Dropdown, type: 'fallNDQ' };
    case 'AllWrestlers':
      return { component: Dropdown, type: 'allWrestlers' };
    case 'Wrestler':
      return { component: Dropdown, type: 'participating' };
    case 'OverUnder':
      return {
        component: ToggleButtons,
        type: 'over/under',
        options: [
          { text: 'Under', value: 'Under', clickable: true },
          { text: extra.value.BaseValue, clickable: false },
          { text: 'Over', value: 'Over', clickable: true },
        ],
      };
    case 'Match': {
      const matchNames = [];
      if (scorecard) {
        matchNames.push(
          ...scorecard.event.Matches.filter(
            (m) => m.MatchId !== matchId.value
          ).map((m) => {
            const name = [m.Title, m.Subtitle, m.ThirdTitle].join(' ');
            return {
              text: name,
              value: name,
            };
          })
        );

        matchNames.push({
          text: 'Unlisted',
          value: 'Unlisted',
        });
      }

      return {
        component: Dropdown,
        type: 'match',
        options: matchNames,
      };
    }
    default:
      return { component: TextField, type: 'text' };
  }
});

const wrestlerStore = useWrestlerStore();
const { isPositiveNumber } = useValidation();

const { displayState, scorecard } = useScorecardStore();

const hint = computed(() => {
  if (viewMode === ScorecardViewMode.Public) {
    return '';
  }
  if (
    displayState === ScorecardDisplayState.View &&
    !resultsMode &&
    (score.value == '0' ||
      score.value === '+0' ||
      score.value === '' ||
      (extra.value.Type === 'Number' && score.value != '5'))
  ) {
    let correct = '';
    switch (extra.value.Type) {
      case 'Wrestler':
      case 'AllWrestlers':
        if (Array.isArray(correctResponse.value.Answer)) {
          const text = 'Any of ';
          const correctArray = Array.from(correctResponse.value.Answer);
          const correctWrestlers = correctArray.map((w) => {
            if (isPositiveNumber(w)) {
              const wrestlerId = parseInt(w.toString(), 10);
              const wrestler = wrestlerStore.wrestlers.find(
                (x) => x.SuperstarId === wrestlerId
              );
              if (wrestler) {
                return wrestler.Name;
              }
            }
            return w;
          });
          correct = `${text} ${correctWrestlers.join(', ')}`;
        } else if (isPositiveNumber(correctResponse.value.Answer)) {
          const wrestlerId = parseInt(
            correctResponse.value.Answer.toString(),
            10
          );
          const found = wrestlerStore.wrestlers.find(
            (w) => w.SuperstarId === wrestlerId
          );
          if (found) {
            correct = found.Name;
          }
        } else {
          correct = correctResponse.value.Answer.toString();
        }
        break;
      case 'Number':
      case 'Yes/No':
      case 'Multi':
      case 'Fall':
      case 'OverUnder':
      default:
        correct = correctResponse.value.Answer.toString();
        break;
    }
    return `Correct: ${correct}`;
  }

  return '';
});

onErrorCaptured((err) => {
  console.error(err);
});

const score = computed(() => {
  if (viewMode === ScorecardViewMode.Public) {
    if (extra.value.Type === 'Number') {
      return `+${points.value + 2}`;
    }
    return `+${points.value}`;
  }
  if (
    response.value.State !== undefined &&
    displayState === ScorecardDisplayState.View &&
    !resultsMode
  ) {
    switch (response.value.State) {
      case 0:
        return '';
      case 1:
        if (extra.value.Type === 'Number') {
          return `+${points.value + 2}`;
        }
        return `+${points.value}`;
      case 2:
        return `+${points.value}`;
      default:
        return '+0';
    }
  }
  return '';
});

function addOption() {
  if (!Array.isArray(response.value.Answer)) {
    response.value.Answer = [response.value.Answer];
  }
  response.value.Answer.push(0);
}

function removeOption(index: number) {
  if (Array.isArray(response.value.Answer)) {
    response.value.Answer.splice(index, 1);
    if (response.value.Answer.length === 1) {
      response.value.Answer = response.value.Answer[0];
    }
  }
}

return (_ctx: any,_cache: any) => {
  const _component_Button = _resolveComponent("Button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (Array.isArray(_unref(answer)))
      ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(_unref(answer), (a, n) => {
          return (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(extraComponent).component), {
            key: a,
            modelValue: _unref(answer)[n],
            "onUpdate:modelValue": ($event: any) => ((_unref(answer)[n]) = $event),
            title: n == 0 ? _unref(extra).Text : '',
            type: _unref(extraComponent).type,
            options: _unref(extra).Options,
            hint: _unref(hint),
            participants: __props.participants,
            items: _unref(extraComponent).options,
            disabled: __props.disabled,
            status: _unref(score),
            "information-text": _unref(extra).UserNotes
          }, {
            badge: _withCtx(() => [
              _createVNode(_component_Button, {
                onClick: ($event: any) => (removeOption(n))
              }, {
                default: _withCtx(() => [
                  _hoisted_2
                ]),
                _: 2
              }, 1032, ["onClick"])
            ]),
            _: 2
          }, 1032, ["modelValue", "onUpdate:modelValue", "title", "type", "options", "hint", "participants", "items", "disabled", "status", "information-text"]))
        }), 128))
      : (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(extraComponent).component), {
          key: 1,
          modelValue: _unref(answer),
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(answer) ? (answer).value = $event : null)),
          title: _unref(extra).Text,
          type: _unref(extraComponent).type,
          options: _unref(extra).Options,
          hint: _unref(hint),
          participants: __props.participants,
          items: _unref(extraComponent).options,
          disabled: __props.disabled,
          status: _unref(score),
          "information-text": _unref(extra).UserNotes
        }, null, 8, ["modelValue", "title", "type", "options", "hint", "participants", "items", "disabled", "status", "information-text"])),
    (_unref(allowMulti))
      ? (_openBlock(), _createBlock(_component_Button, {
          key: 2,
          onClick: addOption
        }, {
          default: _withCtx(() => [
            _hoisted_3
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})